import api from './api';

/**
 * #689: Reconciliation > Insurer receipts. Map a bank credit to an insurer and to broker-months;
 * confirming posts the receipt to the ledger, unmatching reverses it. Amounts are API decimal strings.
 */

export interface Suggestion {
  insurer: string | null;
  insurer_name: string;
  basis: 'utr_settlement' | 'settlement_amount' | 'alias' | 'none';
  alternatives: { insurer: string; insurer_name: string }[];
  allocations: AllocationInput[];
  tds_rate: string | null;
}

export const BASIS_LABEL: Record<Suggestion['basis'], string> = {
  utr_settlement: 'UTR matches a settlement the insurer recorded',
  settlement_amount: 'Amount and date match a recorded settlement',
  alias: 'Narration / UTR looks like earlier payments',
  none: 'No suggestion',
};

export interface AllocationInput {
  month: string; // YYYY-MM-DD (first of month) or YYYY-MM
  amount: string;
  tds_amount?: string;
  statement_batch?: string;
}

export interface QueueRow {
  id: string;
  txn_date: string;
  narration: string;
  reference: string;
  credit: string;
  bank_account_name: string;
  match_state: string;
  age_days: number;
  suggestion: Suggestion | null;
}

export interface PositionRow {
  insurer: string;
  insurer_name: string;
  month: string;
  expected: string | null;
  expected_source: '' | 'approved' | 'statement';
  claimed: string | null;
  received: string;
  tds: string;
  outstanding: string | null;
  unexplained_short: string;
  over_received: string;
  claim_mismatch: boolean;
  batches: { id: string; source_file: string; statement_reference: string; figure: string }[];
}

export interface Position {
  rows: PositionRow[];
  on_account: { insurer: string; insurer_name: string; amount: string }[];
  tds_rate: string | null;
}

export interface Receipt {
  id: string;
  insurer: string;
  insurer_name: string;
  bank_line: string;
  narration: string;
  amount: string;
  received_on: string;
  reference: string;
  status: 'active' | 'reversed';
  journal_entry_number: string;
  allocated: string;
  tds_total: string;
  on_account: string;
  allocations: { month: string; amount: string; tds_amount: string }[];
  reversal_reason: string;
}

interface Listing<T> {
  count: number;
  results: T[];
}

const BASE = '/commissions/insurer-receipts/';

export const insurerReceiptsService = {
  async insurers(): Promise<{ id: string; name: string }[]> {
    return (await api.get(`${BASE}insurers/`)).data;
  },
  async queue(): Promise<Listing<QueueRow>> {
    return (await api.get(`${BASE}queue/`)).data;
  },
  async position(insurer?: string): Promise<Position> {
    return (await api.get(`${BASE}position/`, { params: insurer ? { insurer } : {} })).data;
  },
  async receipts(): Promise<Listing<Receipt>> {
    return (await api.get(BASE)).data;
  },
  async confirm(input: { line: string; insurer: string; allocations: AllocationInput[]; auto_tds?: boolean; note?: string }): Promise<Receipt> {
    return (await api.post(`${BASE}confirm/`, input)).data;
  },
  async unmatch(line: string, reason: string): Promise<Receipt> {
    return (await api.post(`${BASE}unmatch/`, { line, reason })).data;
  },
  async ignore(line: string, reason: string): Promise<void> {
    await api.post(`${BASE}ignore/`, { line, reason });
  },
};
